import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HabitaProcessListItem, KanbanColumnData, PastaStatus } from '../../../shared/models';
import {
  HabitaEmptyState,
  classNames,
} from '../../../shared/components/habita';
import {
  createPastaDetailNavigationState,
  createPastaDetailPath,
  rememberPastaDetailExternalIdsFromItem,
} from '../../../shared/navigation/pastaDetailNavigation';
import {
  formatCPF,
  formatHabitaCreationTime,
  formatHabitaDateTime,
  processStatusUpdatedAtOf,
} from '../../../shared/utils';

function fileCountOf(item: HabitaProcessListItem): number {
  return Number(item.file_count ?? 0);
}

function validatedCountOf(item: HabitaProcessListItem): number {
  return Number(item.completed_file_count ?? 0);
}

function documentProgress(item: HabitaProcessListItem): number {
  const total = Math.max(fileCountOf(item), 1);
  return Math.round((validatedCountOf(item) / total) * 100);
}

function documentTotalLabel(item: HabitaProcessListItem): string {
  const total = fileCountOf(item);
  return `${total} ${total === 1 ? 'documento' : 'documentos'}`;
}

function nameOf(item: HabitaProcessListItem): string {
  return item.customer_name || item.name || `Processo #${item.id}`;
}

function cpfOf(item: HabitaProcessListItem): string {
  return formatCPF(item.customer_cpf ?? '') || '-';
}

function responsavelOf(item: HabitaProcessListItem): string {
  return item.responsible_name?.trim() || item.responsible_email?.trim() || '-';
}

function statusUpdatedAtOf(item: HabitaProcessListItem): string {
  return processStatusUpdatedAtOf(item);
}

const kanbanMutedTextClass = 'text-textPrimary/80';
const kanbanColumnClass = 'flex h-[calc(100vh-16rem)] min-h-[420px] max-h-[720px] w-64 shrink-0 flex-col rounded-lg border border-border-subtle bg-muted/75 p-3';
const kanbanCardClass = 'rounded-lg border border-border-subtle bg-card shadow-[0_1px_2px_rgba(13,53,18,0.04)]';

export interface KanbanColumnRuntimeState {
  total: number;
  hasExplicitTotal: boolean;
  loading: boolean;
  hasNextPage: boolean;
  error: string | null;
  initialized: boolean;
}

export type KanbanColumnRuntimeStateMap = Record<PastaStatus, KanbanColumnRuntimeState>;

export function KanbanSummaryPill({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'warning' | 'success';
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
      : tone === 'warning'
        ? 'border-amber-400/30 bg-amber-500/10 text-amber-700 dark:text-amber-200'
        : 'border-border-subtle bg-card text-textPrimary/80';

  return (
    <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 font-instrument text-sm ${toneClass}`}>
      <span>{label}</span>
      <strong className="font-semibold text-textPrimary">{value}</strong>
    </div>
  );
}

function KanbanCard({
  item,
}: {
  item: HabitaProcessListItem;
}) {
  const progress = documentProgress(item);
  const createdAt = formatHabitaCreationTime(item.created_at);
  const statusUpdatedAt = statusUpdatedAtOf(item);
  const navigate = useNavigate();

  function openPasta() {
    rememberPastaDetailExternalIdsFromItem(item);
    navigate(createPastaDetailPath(item), {
      state: createPastaDetailNavigationState('kanban', item),
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    openPasta();
  }

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={openPasta}
      onKeyDown={handleKeyDown}
      className={classNames(
        'group block cursor-pointer p-3 outline-none transition-colors hover:border-primary focus-visible:ring-2 focus-visible:ring-primary/30',
        kanbanCardClass
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-mono text-[11px] text-textSecondary">#{item.id}</p>
        <span
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-textPrimary/55 transition-colors group-hover:bg-accent group-hover:text-primary"
          aria-hidden
        >
          <i className="ri-arrow-right-s-line text-base" />
        </span>
      </div>
      <h2 className="mt-1 line-clamp-2 font-instrument text-sm font-semibold leading-tight text-textPrimary">
        {nameOf(item)}
      </h2>
      <p className="mt-1 font-instrument text-[11px] text-textSecondary">{cpfOf(item)}</p>

      <div className="mt-3 flex items-center justify-between gap-2 font-instrument text-[11px] text-textSecondary">
        <span>{documentTotalLabel(item)}</span>
        <span className="truncate">{responsavelOf(item)}</span>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" title={`${progress}% dos documentos validados`}>
        <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 font-instrument text-[11px] text-textSecondary">
        <span>Tempo de criação</span>
        <span title={createdAt.title}>
          {createdAt.label}
        </span>
      </div>

      <div className="mt-1 flex items-center justify-between gap-2 font-instrument text-[11px] text-textSecondary">
        <span className="shrink-0">Status atualizado</span>
        <span className="min-w-0 truncate text-right" title={formatHabitaDateTime(statusUpdatedAt)}>
          {formatHabitaDateTime(statusUpdatedAt)}
        </span>
      </div>
    </article>
  );
}

function KanbanColumn({
  column,
  columnState,
  hasFilters,
  onLoadMore,
}: {
  column: KanbanColumnData;
  columnState: KanbanColumnRuntimeState;
  hasFilters: boolean;
  onLoadMore: (status: PastaStatus) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadedCount = column.items.length;
  const hasServerTotal = columnState.initialized && columnState.hasExplicitTotal;
  const countLabel = !columnState.initialized && columnState.loading
    ? '...'
    : hasServerTotal
        ? String(columnState.total)
        : columnState.hasNextPage
          ? `${loadedCount}+`
          : String(loadedCount);
  const countTitle = !columnState.initialized && columnState.loading
    ? 'Carregando total'
    : hasServerTotal
      ? `${loadedCount} carregados de ${columnState.total}${hasFilters ? ' com filtros ativos' : ''}`
      : `${loadedCount} carregados${columnState.hasNextPage ? ' ou mais' : ''}`;

  useEffect(() => {
    const root = scrollContainerRef.current;
    const target = loadMoreRef.current;

    if (!root || !target || !columnState.hasNextPage || columnState.loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore(column.status);
        }
      },
      {
        root,
        rootMargin: '120px 0px',
        threshold: 0.1,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [column.status, columnState.hasNextPage, columnState.loading, onLoadMore]);

  return (
    <section className={kanbanColumnClass}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className={`font-instrument text-xs font-semibold uppercase tracking-wide ${kanbanMutedTextClass}`}>
            {column.label}
          </h3>
          <p className="mt-0.5 line-clamp-2 font-instrument text-[11px] leading-snug text-textPrimary/70">
            {column.description}
          </p>
        </div>
        <span
          className="shrink-0 rounded-full bg-secondary px-2 py-0.5 font-instrument text-xs font-semibold text-secondary-foreground"
          title={countTitle}
          aria-label={countTitle}
        >
          {countLabel}
        </span>
      </div>

      <div ref={scrollContainerRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
        {column.items.length === 0 && !columnState.loading && !columnState.error ? (
          <div className="rounded-md border border-dashed border-border-subtle py-6 text-center font-instrument text-xs text-textPrimary/60">
            Nada por aqui
          </div>
        ) : column.items.length > 0 ? (
          column.items.map((item) => (
            <KanbanCard
              key={item.id}
              item={item}
            />
          ))
        ) : (
          null
        )}
        {columnState.error && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-center">
            <p className="font-instrument text-xs font-medium text-amber-900">
              {columnState.error}
            </p>
            <button
              type="button"
              onClick={() => onLoadMore(column.status)}
              className="mt-2 inline-flex h-8 items-center justify-center rounded-md border border-amber-300/50 bg-card px-3 font-instrument text-xs font-medium text-amber-900 transition-colors hover:border-primary hover:text-textPrimary dark:text-amber-100"
            >
              Tentar novamente
            </button>
          </div>
        )}
        {columnState.loading && (
          <div className={classNames(kanbanCardClass, 'p-3')} aria-live="polite">
            <div className="h-3 w-16 animate-pulse rounded bg-muted" />
            <div className="mt-3 h-4 w-full animate-pulse rounded bg-muted" />
            <div className="mt-2 h-3 w-28 animate-pulse rounded bg-muted/70" />
          </div>
        )}
        <div ref={loadMoreRef} className="h-1" aria-hidden />
      </div>
    </section>
  );
}

export function KanbanBoard({
  columns,
  columnPageState,
  hasFilters,
  onLoadMoreColumn,
}: {
  columns: KanbanColumnData[];
  columnPageState: KanbanColumnRuntimeStateMap;
  hasFilters: boolean;
  onLoadMoreColumn: (status: PastaStatus) => void;
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-3">
        {columns.map((column) => (
          <KanbanColumn
            key={column.status}
            column={column}
            columnState={columnPageState[column.status]}
            hasFilters={hasFilters}
            onLoadMore={onLoadMoreColumn}
          />
        ))}
      </div>
    </div>
  );
}

export function KanbanGlobalLoading() {
  return (
    <div
      className="flex min-h-[420px] items-center justify-center rounded-lg border border-border-subtle bg-card/80 p-6"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-full max-w-md text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-textPrimary">
          <i className="ri-loader-4-line animate-spin text-2xl" aria-hidden />
        </div>
        <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">
          Carregando Kanban
        </p>
        <p className="mt-1 font-instrument text-xs text-textSecondary">
          Buscando colunas.
        </p>
        <div className="mt-5 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((slot) => (
            <div key={slot} className="rounded-lg border border-border-subtle bg-muted p-3">
              <div className="h-3 w-14 animate-pulse rounded bg-background/60" />
              <div className="mt-3 h-4 w-full animate-pulse rounded bg-background/60" />
              <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-background/60" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function KanbanEmptyState() {
  return (
    <HabitaEmptyState
      icon="ri-layout-grid-line"
      title="Nenhuma pasta no Kanban"
      description="As pastas aparecerão aqui quando disponíveis."
    />
  );
}
